import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MainNav from '../Common/MainNav';
import './Messages.css'; // Ensure this import is present for CSS styles
import { fetchOrders, fetchMessages, sendMessage, updateOrderStatus } from '../API/API_Handler'; // Import the API functions

const Messages = () => {
  const [orders, setOrders] = useState([]); // Store detailed order information
  const [selectedOrder, setSelectedOrder] = useState(null); // Currently selected order
  const [messages, setMessages] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // States for message input and file upload
  const [newMessage, setNewMessage] = useState('');
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [sending, setSending] = useState(false);

  // Get email from session storage
  const [ssoUserEmail, setSsoUserEmail] = useState('');

  // Function to fetch order details for the user (practice) by email
  const _fetchOrders = async () => {
    setLoadingOrders(true);
    try {
      const fetchedOrders = await fetchOrders();
      setOrders(fetchedOrders);
      // Automatically select the first order if none is selected
      if (fetchedOrders.length > 0 && !selectedOrder) {
        setSelectedOrder(fetchedOrders[0]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  // Function to fetch messages for the selected order
  const _fetchMessages = async (orderId) => {
    setLoadingMessages(true);
    try {
      // Await the fetchMessages call
      const fetchedMessages = await fetchMessages(orderId);
      setMessages(fetchedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Function to send a new message and update order status
  const _sendMessage = async () => {
    if (!newMessage.trim()) return; // Prevent sending empty messages
    setSending(true);
    try {
      // Send the message
      const createdMessage = await sendMessage(selectedOrder.id, newMessage);
      // Option 1: Append the new message to the existing messages
      setMessages((prevMessages) => [...prevMessages, createdMessage]);

      // Option 2: Re-fetch messages to ensure synchronization
      // await _fetchMessages(selectedOrder.id);

      // Update the order status
      const updatedOrder = await updateOrderStatus(selectedOrder.id, 'AWAITING VENDOR ACTION');
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === updatedOrder.id ? { ...order, status: updatedOrder.status } : order
        )
      );
      // If the updated order is the currently selected one, update its status in selectedOrder
      if (selectedOrder && selectedOrder.id === updatedOrder.id) {
        setSelectedOrder((prevOrder) => ({ ...prevOrder, status: updatedOrder.status }));
      }
      // Clear the input fields
      setNewMessage('');
      setAttachmentFile(null);
    } catch (error) {
      console.error('Error sending message or updating order status:', error);
      // Optionally, implement user-facing error handling here (e.g., notifications)
    } finally {
      setSending(false);
    }
  };

  // Fetch orders when the component mounts
  useEffect(() => {
    _fetchOrders();
  }, []);

  // Fetch messages when the selected order changes
  useEffect(() => {
    if (selectedOrder) {
      _fetchMessages(selectedOrder.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder]);

  // Function to handle order selection
  const handleOrderClick = (order) => {
    setSelectedOrder(order);
  };

  // Function to determine status highlight color
  const getStatusClass = (status) => {
    switch (status) {
      case 'PENDING':
        return 'status-pending';
      case 'AWAITING VENDOR ACTION':
        return 'status-awaiting-vendor';
      case 'AWAITING USER ACTION':
        return 'status-awaiting-user';
      case 'CLOSED':
        return 'status-closed';
      default:
        return '';
    }
  };

  return (
    <div>
      <MainNav navbarColor={"#26051C"} />

      {orders.length === 0 ? (
        <div className='user-no-messages-error'>
          Whoops looks like you haven't placed any product requests!
          If you think this is an error, please contact support.
        </div>
      ) : (
        <div className="user-messages-container">
          {/* Left Panel: Orders List */}
          <div className="user-orders-list-panel">
            <div className='orders-header font-all-round-gothic-bold'>Orders</div>
            {loadingOrders ? (
              <div className="user-loading">Loading orders...</div>
            ) : (
              <div>
                {orders.map((order) => (
                  <div
                    key={order.id}
                    className={`user-order-item ${order.id === (selectedOrder ? selectedOrder.id : null) ? 'order-item-active' : ''}`}
                    onClick={() => handleOrderClick(order)}
                  >
                    <div className="user-order-details">
                      <div className="user-order-id">#{order.id}</div>
                      <div className="user-order-product">Product Name: {order.product.name}</div>
                      <div className={`order-status ${getStatusClass(order.status)}`}>
                        Status: {order.status}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel: Messages */}
          <div className="user-messages-area-panel">
            {selectedOrder ? (
              <>
                <div className="user-messages-header">
                  <div>Order #{selectedOrder.id}</div>
                </div>
                <div className="user-messages-content">
                  {loadingMessages ? (
                    <div className="user-loading">Loading messages...</div>
                  ) : (
                    <div className="user-messages-list">
                      {messages.length > 0 ? (
                        messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`ticket-message ${/^\d+$/.test(msg.sender)? 'vendor' : 'user'}`}
                          >
                            <div className="user-message-header">
                              <span className="user-sender">{/^\d+$/.test(msg.sender) ? 'Vendor' : "You"}</span>
                              <span className="user-timestamp">
                                {new Date(msg.created_at).toLocaleString()}
                              </span>
                            </div>
                            <div className="user-message-body">
                              <p>{msg.message}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-messages">No messages yet. Start the conversation!</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="user-message-input">
                  <textarea
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    rows="1"
                    className='user-message-textarea'
                  />
                  <div className="user-message-actions">
                    <button
                      onClick={_sendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="user-send-button"
                    >
                      {sending ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="user-no-selection">Select an order to view messages.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
